"use client";

import { useState } from "react";
import { Minus, Plus, X } from "lucide-react";
import { useTranslations } from "next-intl";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useCurrency } from "@/contexts/CurrencyContext";
import {
  calculateRentalDays,
  calculateSeasonalDailyV2Price,
  calculateSeasonalRentalPrice,
  getSeasonalTimeslotPrice,
} from "@/lib/utils/pricing";
import { getDailyV2TiersForSub } from "@/lib/utils/daily-matrix";
import {
  customerPolicyTotalFromTable,
  type CustomerPolicyTable,
} from "@/lib/pricing/base-plus-extra";
import type { CartItem } from "@/types/booking";
import type { PricingSeason, ProductPricing } from "@/types";
import type { ProductDayPriceRow } from "@/types/api/products";
import type { SupportedCurrency } from "@/lib/utils/currency";

/**
 * 선택 내역 행의 제목·부제 위계 (08-18 확정).
 *
 * 상품 상세 페이지 안이라 대표 상품명은 중복 정보 — 세부상품명이 있으면(=v2 라인)
 * 그쪽을 제목으로 올리고, 부제에는 기간/옵션 라벨만 남긴다.
 * 세부상품명이 없으면(v1 daily·v1 timeslot·단일 무명 세부상품 생략 규칙)
 * 기존대로 제목 = 대표 상품명, 부제 = 기간 텍스트.
 */
export function resolveRentalLabels({
  subProductName,
  productName,
  fallbackSubtitle,
}: {
  subProductName?: string | null;
  productName: string;
  fallbackSubtitle?: string | null;
}): { title: string; subtitle: string | null } {
  const sub = subProductName?.trim();
  const subtitle = fallbackSubtitle?.trim() || null;
  return { title: sub || productName, subtitle };
}

interface CartItemsListProps {
  cartItems: CartItem[];
  pricingMode: "daily" | "timeslot";
  productName: string;
  pricing: ProductPricing[];
  seasons?: PricingSeason[];
  /** Daily v2 세부 상품 티어(product.day_prices) — v2 라인 시즌 재가격의 티어 소스 */
  dayPrices?: ProductDayPriceRow[];
  /** 기간 요금 고객가 표(서버 계산, 요율·공급가 없음) — product.policyTable */
  policyTable?: CustomerPolicyTable | null;
  sourceCurrency: SupportedCurrency;
  totalPrice: number;
  /** 하루당 옵션(09-30)에 곱할 담긴 대여 일수 합 — 없으면 null(날짜 전) */
  perDayDays?: number | null;
  onQuantityChange: (itemId: string, delta: number) => void;
  onRemove: (itemId: string) => void;
}

export default function CartItemsList({
  cartItems,
  pricingMode,
  productName,
  pricing,
  seasons,
  dayPrices,
  policyTable,
  sourceCurrency,
  totalPrice,
  perDayDays = null,
  onQuantityChange,
  onRemove,
}: CartItemsListProps) {
  const t = useTranslations("product.booking");
  const tPerDay = useTranslations("product.perDayOption");
  const { formatFromSource } = useCurrency();

  if (cartItems.length === 0) return null;

  return (
    <div className="space-y-2">
      <p className="text-sm font-semibold text-gray-700 pt-2">
        {t("selectedItems")}
      </p>

      {/* 연회색 박스: 항목들 + 총액. active 테두리 없음. */}
      <div className="bg-gray-50 rounded-md border border-gray-200 divide-y divide-gray-200 overflow-hidden">
        {cartItems.map((item) => {
          let itemPrice = 0;
          let durationText: string | null = null;

          if (item.type === "rental") {
            if (item.optionDisplayOrder != null && item.unitPrice != null) {
              // Timeslot v2 (Phase B-3): 셀 단가 × 수량. 성수기는 (sub, option)
              // 행으로 재정의(09-21) — useBookingCart.totalPrice와 같은 함수.
              // 세부상품명은 제목으로 승격(08-18)돼 부제는 옵션 라벨만 남는다.
              itemPrice =
                getSeasonalTimeslotPrice(
                  item.unitPrice,
                  item.optionDisplayOrder,
                  item.pickupDate,
                  seasons,
                  item.subDisplayOrder ?? null,
                ).total * item.quantity;
              durationText = item.timeOptionLabel || null;
            } else if (pricingMode === "daily" && item.pickupDate && item.returnDate) {
              const days = calculateRentalDays(
                item.pickupDate,
                item.returnDate,
              );
              if (item.subDisplayOrder != null && item.unitPrice != null) {
                // Daily v2 (08-17): 세부 상품 티어 단가 × 일수 × 수량. 성수기는
                // (sub, tier) 행으로 일별 재정의(09-21). v2 상품은 product_pricing이
                // 비어 v1 계산이 0원이 되므로 티어는 day_prices에서 가져온다.
                const policyTotal = customerPolicyTotalFromTable({
                  table: policyTable,
                  subDisplayOrder: item.subDisplayOrder,
                  days,
                  pickupDate: item.pickupDate,
                  markedSeasons: seasons,
                });
                itemPrice = policyTotal == null ? calculateSeasonalDailyV2Price(
                  item.pickupDate,
                  item.returnDate,
                  item.quantity,
                  getDailyV2TiersForSub(dayPrices, item.subDisplayOrder),
                  item.subDisplayOrder,
                  seasons,
                ).total : policyTotal * item.quantity;
                // 세부상품명은 제목으로 승격(08-18) — 부제는 일수만.
                durationText = t("days", { count: days });
              } else {
                itemPrice = calculateSeasonalRentalPrice(
                  item.pickupDate,
                  item.returnDate,
                  item.quantity,
                  pricing,
                  seasons,
                ).total;
                durationText = t("days", { count: days });
              }
            } else if (
              pricingMode === "timeslot" &&
              item.pickupDate &&
              item.timeSlotPrice
            ) {
              itemPrice =
                getSeasonalTimeslotPrice(
                  item.timeSlotPrice,
                  item.timeSlotDisplayOrder,
                  item.pickupDate,
                  seasons,
                  null,
                ).total * item.quantity;
              // 라벨이 있으면 "라벨 · 시간"으로 함께 표시 (라벨만/시간만도 처리)
              durationText =
                [
                  item.timeSlotLabel,
                  item.timeSlotDuration
                    ? t("hours", { count: item.timeSlotDuration })
                    : null,
                ]
                  .filter(Boolean)
                  .join(" · ") || null;
            }
          } else if (item.type === "option") {
            // 하루당 옵션(09-30): 수량 대신 담긴 대여 일수 — useBookingCart.totalPrice 와 같은 규칙
            itemPrice =
              (item.optionPrice || 0) *
              (item.perDay ? (perDayDays ?? item.quantity) : item.quantity);
          }

          const isRental = item.type === "rental";
          // v2 라인이면 세부상품명이 제목, 아니면 대표 상품명(v1 무회귀).
          const { title, subtitle } = resolveRentalLabels({
            subProductName: item.subProductName,
            productName,
            fallbackSubtitle: durationText,
          });

          return (
            <div key={item.id} className="px-3 py-3">
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {isRental
                      ? title
                      : `${item.optionName} - ${item.optionValue}`}
                  </p>
                  {/* 날짜/지점/시간은 상단 요약 박스에 — 여기선 기간/옵션 라벨만 부제로 */}
                  {isRental && subtitle && (
                    <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 -mt-1 shrink-0"
                  onClick={() => onRemove(item.id)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>

              <div className="flex items-center justify-between">
                {item.perDay ? (
                  // 하루당 옵션: 수량 입력 없음 — "단가 × N일"(날짜 전이면 "단가 / 일")
                  <span className="text-sm text-gray-600 tabular-nums">
                    {perDayDays != null
                      ? tPerDay("unitTimesDays", {
                          price: formatFromSource(item.optionPrice || 0, sourceCurrency),
                          days: perDayDays,
                        })
                      : tPerDay("unitPrice", {
                          price: formatFromSource(item.optionPrice || 0, sourceCurrency),
                        })}
                  </span>
                ) : (
                /* 네이버식 수량 stepper: 박스 1개 + 내부 구분선 */
                <div className="flex items-center rounded-md border border-gray-300 bg-white overflow-hidden">
                  <button
                    type="button"
                    aria-label={t("decreaseQuantity")}
                    disabled={item.quantity <= 1}
                    onClick={() => onQuantityChange(item.id, -1)}
                    className="flex h-8 w-9 items-center justify-center text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white"
                  >
                    <Minus className="h-3.5 w-3.5" />
                  </button>
                  <QuantityField
                    quantity={item.quantity}
                    max={item.maxQuantity ?? null}
                    label={t("quantityInput")}
                    onCommit={(next) =>
                      next !== item.quantity &&
                      onQuantityChange(item.id, next - item.quantity)
                    }
                  />
                  <button
                    type="button"
                    aria-label={t("increaseQuantity")}
                    // Timeslot v2: 잔여 재고 도달 시 + 비활성 (maxQuantity 없음 = 무제한, v1 불변)
                    disabled={
                      item.maxQuantity != null &&
                      item.quantity >= item.maxQuantity
                    }
                    onClick={() => onQuantityChange(item.id, 1)}
                    className="flex h-8 w-9 items-center justify-center text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white"
                  >
                    <Plus className="h-3.5 w-3.5" />
                  </button>
                </div>
                )}

                <span className="text-base font-bold">
                  {formatFromSource(itemPrice, sourceCurrency)}
                </span>
              </div>

              {/* 수량이 커질 수 있는 항목(상한 없음/10 이상)에만 직접 입력 힌트 —
                  편도 반납 요금(수량 = km) 같은 옵션용. 장비 2개 상한 같은 건 조용히. */}
              {item.type === "option" &&
                !item.perDay &&
                (item.maxQuantity == null || item.maxQuantity >= 10) && (
                  <p className="mt-1.5 break-keep text-xs text-gray-500">
                    {t("quantityInputHint")}
                  </p>
                )}

              {/* 구매 수량 제한 도달 인라인 문구 (08-10, A안 — 시안
                  docs/preview-booking-max-quantity.html). purchaseLimit 유래일 때만 —
                  재고(maxQuantity) 도달은 기존 잔여 배지 관례 유지. */}
              {item.purchaseLimit != null &&
                item.quantity >= item.purchaseLimit && (
                  <p className="mt-1.5 break-keep text-xs text-gray-500">
                    {t("maxPurchaseLimitNotice", {
                      count: item.purchaseLimit,
                    })}
                  </p>
                )}
            </div>
          );
        })}

        {/* 총액 행 (박스 하단) */}
        <div className="flex items-center justify-between px-3 py-3 bg-white">
          <span className="text-sm font-medium text-gray-700">
            {t("totalItems", { count: cartItems.length })}
          </span>
          <span className="text-lg font-bold text-orange-600">
            {formatFromSource(totalPrice, sourceCurrency)}
          </span>
        </div>
      </div>
    </div>
  );
}

/**
 * 스테퍼 가운데 숫자 — 누르면 입력 칸으로 바뀐다(09-22). 편도 반납 요금처럼
 * 수량이 수백(km)까지 가는 옵션을 −/+ 로만 채울 수 없어서. 확정은 Enter/blur,
 * Esc 는 취소. 범위는 상위 핸들러(useBookingCart.updateCartItemQuantity)와 같은
 * [1, maxQuantity] 로 여기서도 자른다.
 */
function QuantityField({
  quantity,
  max,
  label,
  onCommit,
}: {
  quantity: number;
  max: number | null;
  label: string;
  onCommit: (next: number) => void;
}) {
  const [draft, setDraft] = useState<string | null>(null);

  const commit = () => {
    if (draft === null) return;
    const parsed = Number.parseInt(draft, 10);
    setDraft(null);
    if (!Number.isFinite(parsed)) return;
    onCommit(Math.min(Math.max(1, parsed), max ?? Infinity));
  };

  if (draft === null) {
    return (
      <button
        type="button"
        aria-label={label}
        onClick={() => setDraft(String(quantity))}
        className="flex h-8 min-w-10 items-center justify-center border-x border-gray-300 px-1 text-sm font-medium tabular-nums hover:bg-gray-50"
      >
        {quantity}
      </button>
    );
  }

  return (
    <input
      type="text"
      inputMode="numeric"
      pattern="[0-9]*"
      aria-label={label}
      autoFocus
      value={draft}
      onChange={(e) => setDraft(e.target.value.replace(/[^0-9]/g, ""))}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          commit();
        } else if (e.key === "Escape") {
          e.preventDefault();
          setDraft(null);
        }
      }}
      onFocus={(e) => e.currentTarget.select()}
      className="h-8 w-14 border-x border-gray-300 bg-white text-center text-sm font-medium tabular-nums outline-none ring-2 ring-inset ring-gray-900"
    />
  );
}
